<template>
	<div class="row mb-5">
		<div class="col-12 col-md-4">
			<label for="webcam" class="fw-bold">Webcam</label>
			<select
				v-model="webcam_selected"
				id="webcam"
				class="form-control form-select select2">
				<option
					v-for="webcam in webcams"
					:value="webcam"
					:key="webcam.uuid"
					:selected="webcam.selected">
					{{ webcam.name }}
				</option>
			</select>
		</div>
		<div class="col-12 col-md-8">
			<label for="webcam-option" class="fw-bold"
				>Opção de configuração</label
			>
			<select
				v-model="webcam_option_selected"
				id="webcam-option"
				class="form-control form-select select2">
				<option
					v-for="option in webcam_selected_options"
					:value="option"
					:key="option.uuid"
					:selected="option.selected">
					Resolução min: {{ option.min_resolution }} / Resolução max:
					{{ option.max_resolution }}, Fps min:
					{{ option.fps_min_resolution }} / Fps max:
					{{ option.fps_max_resolution }}
				</option>
			</select>
		</div>
	</div>

	<div class="row mb-8">
		<div class="col-12 col-md-4">
			<label for="microphone" class="fw-bold">Microfone</label>
			<select
				v-model="microphone_selected"
				id="microphone"
				class="form-control form-select select2">
				<option
					v-for="microphone in microphones"
					:value="microphone"
					:key="microphone.uuid"
					:selected="microphone.selected">
					{{ microphone.name }}
				</option>
			</select>
		</div>
		<div class="col-12 col-md-8">
			<label for="microphone-option" class="fw-bold"
				>Opção de configuração</label
			>
			<select
				v-model="microphone_option_selected"
				id="microphone-option"
				class="form-control form-select select2">
				<option
					v-for="microphone in microphone_selected_options"
					:value="microphone"
					:key="microphone.uuid"
					:selected="microphone.selected">
					Canais minímos: {{ microphone.min_channels }} / Canais
					máximos: {{ microphone.max_channels }}
				</option>
			</select>
		</div>
	</div>

    <div class="row mb-8">
        <div class="col-12">
            <label for="microphone" class="fw-bold">Leitor Biométrico</label>
            <select
                v-model="biometry_device_type"
                id="microphone"
                class="form-control form-select select2">
                <option
                    v-for="device in [
                        {
                            name: 'Hamster',
                            value: 'nitgen_ramster3',
                        },
                        {
                            name: 'Futronic F80H',
                            value: 'futronic_f80h',
                        }
                    ]"
                    :value="device.value"
                    :key="device.value">
                    {{ device.name }}
                </option>
            </select>
        </div>
    </div>

	<div class="row">
		<div class="col-12">
			<loading_button
				:loading="loading"
				:action="saveHardwareConfiguration"
				>Salvar configuração de hardwares
			</loading_button>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { Ref, computed, inject, onMounted, ref } from 'vue';
	import axios from 'axios';
	import ComputerHardware from '../../Interfaces/ComputerHardware';
	import Microphone from '../../Interfaces/Microphone';
	import MicrophoneConfigurationOption from '../../Interfaces/MicrophoneConfigurationOption';
	import Webcam from '../../Interfaces/Webcam';
	import WebcamConfigurationOption from '../../Interfaces/WebcamConfigurationOption';
	import loading_button from '../Widgets/loading_button.vue';
    import { Computer } from '../../Interfaces/Computer';

	const props = defineProps({
		computer_uid: { type: String, required: true },
	});

	const loading = ref(false);

	const computer_hardware = inject('computer_hardware') as ComputerHardware;
	const microphones = computer_hardware.microphones;
	const webcams = computer_hardware.webcams;

	const webcam_selected = ref({}) as Ref<Webcam>;
	const webcam_selected_options = computed(
		() => webcam_selected.value.options
	);
	const webcam_option_selected = ref({}) as Ref<WebcamConfigurationOption>;

	const microphone_selected = ref({}) as Ref<Microphone>;
	const microphone_selected_options = computed(
		() => microphone_selected.value.options
	);
	const microphone_option_selected = ref(
		{}
	) as Ref<MicrophoneConfigurationOption>;

	const save_hardware_configuration_url = inject(
		'save_hardware_configuration_url'
	) as string;

	const emitter = inject('emitter');

    const computer = inject('computer') as Computer

    const biometry_device_type = ref(computer.biometry_device_type)

	async function saveHardwareConfiguration() {
		loading.value = true;
		axios
			.post(save_hardware_configuration_url, {
				uid: props.computer_uid,
				webcam_selected_uuid: webcam_selected.value.uuid,
				webcam_option_selected_uuid: webcam_option_selected.value.uuid,
				microphone_selected_uuid: microphone_selected.value.uuid,
				microphone_option_selected_uuid: microphone_option_selected.value.uuid,
                biometry_device_type: biometry_device_type.value
			})
			.then((response) => {
				//@ts-ignore
				toastr.success('Configurações de hardwares salvas !');
				//@ts-ignore
				emitter.emit('webcam_options_changed');
			})
			.catch((response) => {
				const error = JSON.parse(response.request.response).message;
				//@ts-ignore
				toastr.error(
					'Ocorreu um erro ao salvar as configurações de hardwares, Erro:' +
						error
				);
			})
			.finally(() => (loading.value = false));
	}

	onMounted(() => {
		const webcam_array = webcams ? Object.values(webcams) : null;
		const microphone_array = microphones
			? Object.values(microphones)
			: null;

		const webcam_found = webcam_array?.find((item) => item.selected);
		const microphone_found = microphone_array?.find(
			(item) => item.selected
		);

		if (webcam_found) {
			webcam_selected.value = webcam_found as Webcam;

			const webcam_array_selected_options = Object.values(
				webcam_selected_options.value
			);

			webcam_option_selected.value = webcam_array_selected_options.find(
				(item) => item.selected
			) as WebcamConfigurationOption;
		}

		if (microphone_found) {
			microphone_selected.value = microphone_found as Microphone;

			const microphone_array_selected_options = Object.values(
				microphone_selected_options.value
			);

			microphone_option_selected.value =
				microphone_array_selected_options.find(
					(item) => item.selected
				) as MicrophoneConfigurationOption;
		}
	});
</script>
